import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";

import { useAcknowledgeLevelMutation } from "../hooks/useAcknowledgeLevelMutation";
import {
  ParsedUnacknowledgedLevel,
  useGetUnacknowledgedLevels,
} from "../hooks/useGetUnacknowledgedLevels";

import { levelCongratulationsContext } from "./LevelCongratulationsContext";

type LevelsCongratulationsProviderProps = {
  children: React.ReactNode;
};

export const LevelsCongratulationsProvider: React.FC<LevelsCongratulationsProviderProps> = ({
  children,
}) => {
  const {
    data: unacknowledgedLevels,
    refetch: refetchUnacknowledgedLevels,
    isFetching: areUnacknowledgedLevelsFetching,
  } = useGetUnacknowledgedLevels();
  const acknowledgeLevelMutation = useAcknowledgeLevelMutation();
  const history = useHistory();

  const areStatusesPending =
    areUnacknowledgedLevelsFetching ||
    acknowledgeLevelMutation.status === "loading";

  const [
    currentUnacknowledgedLevel,
    setCurrentUnacknowledgedLevel,
  ] = useState<ParsedUnacknowledgedLevel | null>(null);

  useEffect(() => {
    if (unacknowledgedLevels?.length) {
      setCurrentUnacknowledgedLevel(unacknowledgedLevels[0]);
      history.push(authenticatedRouting.buildUrl("levelCongratulations"));
    } else {
      setCurrentUnacknowledgedLevel(null);
    }
  }, [unacknowledgedLevels, history]);

  const acknowledgeLevel = useCallback(
    async (levelId: string, levelGroupId: string) => {
      await acknowledgeLevelMutation.mutateAsync({
        levelId,
        levelGroupId,
      });
      await refetchUnacknowledgedLevels();
    },
    [refetchUnacknowledgedLevels, acknowledgeLevelMutation]
  );

  return (
    <levelCongratulationsContext.Provider
      value={{
        currentUnacknowledgedLevel,
        acknowledgeLevel,
        areStatusesPending,
      }}
    >
      {children}
    </levelCongratulationsContext.Provider>
  );
};
